import {
  ReebeloConditions,
  ReebeloLanguage,
  TagHelpers,
  REEBELO_STORE_TO_LANGUAGE,
} from '@lambda/reebelo';
import { convertCellularFieldsToServiceProvider } from '@lambda/business/offers/cellular';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { get as _get, groupBy, pick, pickBy, sortBy } from 'lodash-es';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import settings from '@/settings';
import { ProductVariant } from '@/components/collections/[handle]/NewProductPage/v2/Variants/types';

export const isNaStore =
  settings.store === 'reebelo-us' || settings.store === 'reebelo-ca';

const Conditions: Record<ReebeloLanguage, Record<ReebeloConditions, string>> = {
  en: {
    'Brand New': 'Brand New',
    'Like New': 'Like New',
    Premium: 'Premium',
    Pristine: 'Pristine',
    Excellent: 'Excellent',
    Good: 'Good',
    Acceptable: 'Acceptable',
  },
  ko: {
    'Brand New': '새상품',
    'Like New': '거의 새것과 같은',
    Premium: '프리미엄',
    Pristine: 'S급',
    Excellent: 'A급',
    Good: 'B급',
    Acceptable: 'C급',
  },
  zh: {
    'Brand New': '全新品',
    'Like New': '几乎全新',
    Premium: '優質',
    Pristine: '5星品',
    Excellent: '4.5星品',
    Good: '4星品',
    Acceptable: '3.5星品',
  },
  fr: {
    'Brand New': 'Neuf',
    'Like New': 'Comme Neuf',
    Premium: 'Premium',
    Pristine: 'Pristine',
    Excellent: 'Excellent',
    Good: 'Bon',
    Acceptable: 'Acceptable',
  },
  de: {
    'Brand New': 'Neu',
    'Like New': 'Wie neu',
    Premium: 'Premium',
    Pristine: 'Makellos',
    Excellent: 'Ausgezeichnet',
    Good: 'Gut',
    Acceptable: 'Akzeptabel',
  },
  es: {
    'Brand New': 'Nuevo',
    'Like New': 'Como nuevo',
    Premium: 'Premium',
    Pristine: 'Impecable',
    Excellent: 'Excelente',
    Good: 'Bueno',
    Acceptable: 'Aceptable',
  },
  it: {
    'Brand New': 'Nuovo',
    'Like New': 'Come nuovo',
    Premium: 'Premium',
    Pristine: 'Impeccabile',
    Excellent: 'Eccellente',
    Good: 'Buono',
    Acceptable: 'Accettabile',
  },
  // AI translation pending native review (RT-17323).
  sv: {
    'Brand New': 'Helt ny',
    'Like New': 'Som ny',
    Premium: 'Premium',
    Pristine: 'Felfri',
    Excellent: 'Utmärkt',
    Good: 'Bra',
    Acceptable: 'Godkänt',
  },
  nl: {
    'Brand New': 'Nieuw',
    'Like New': 'Als nieuw',
    Premium: 'Premium',
    Pristine: 'Onberispelijk',
    Excellent: 'Uitstekend',
    Good: 'Goed',
    Acceptable: 'Acceptabel',
  },
  pt: {
    'Brand New': 'Novo',
    'Like New': 'Como novo',
    Premium: 'Premium',
    Pristine: 'Impecável',
    Excellent: 'Excelente',
    Good: 'Bom',
    Acceptable: 'Aceitável',
  },
  fi: {
    'Brand New': 'Uusi',
    'Like New': 'Kuin uusi',
    Premium: 'Premium',
    Pristine: 'Moitteeton',
    Excellent: 'Erinomainen',
    Good: 'Hyvä',
    Acceptable: 'Tyydyttävä',
  },
};

export const localizeCondition = (
  value: string,
  language: ReebeloLanguage = REEBELO_STORE_TO_LANGUAGE[settings.store],
): string => Conditions[language]?.[value as ReebeloConditions] ?? value;

type VariantNames =
  | 'serviceProvider'
  | 'screenSize'
  | 'color'
  | 'storage'
  | 'batteryHealth'
  | 'condition'
  | 'cpu'
  | 'ram'
  | 'simSlot'
  | 'connectivity'
  | 'watchFaceSize'
  | 'watchCaseMaterial'
  | 'watchCaseColor'
  | 'watchBandType'
  | 'watchBandColor'
  | 'watchBandMaterial'
  | 'charger'
  | 'touchbar'
  | 'version'
  | 'language';

const VARIANTS: VariantNames[] = [
  'serviceProvider',
  'screenSize',
  'connectivity',
  'storage',
  'color',
  'cpu',
  'ram',
  'simSlot',
  'charger',
  'touchbar',
  'version',
  'language',
  'condition',
  'batteryHealth',
];
const SMARTWATCHVARIANTS: VariantNames[] = [
  'watchCaseMaterial',
  'watchFaceSize',
  'watchCaseColor',
  'connectivity',
  'watchBandMaterial',
  'watchBandType',
  'watchBandColor',
  'condition',
  'batteryHealth',
  'storage',
];

const LAPTOPSVARIANTS: VariantNames[] = [
  'cpu', // 0
  'ram', // 1
  'storage', // 2
  'color', // 3
  'screenSize', // 4
  'charger', // 5
  'touchbar', // 6
  'version', // 7
  'connectivity', // 8
  'condition', // 9
  'batteryHealth', // 10
];

type VariantObj = Record<VariantNames, string>;

type SelectionT = Omit<Partial<VariantObj>, 'serviceProviders'> & {
  serviceProvider?: string;
};

export const tagToSelection = (
  tags: string[],
  dynamicVariants?: string[] | null,
): SelectionT => {
  const extracted = TagHelpers.extractTags(tags);
  const serviceProvider = convertCellularFieldsToServiceProvider(extracted);
  const variants =
    extracted.category === 'Smartwatches'
      ? SMARTWATCHVARIANTS
      : dynamicVariants ?? [...VARIANTS];
  const allObject = pick(
    { ...extracted, serviceProvider: serviceProvider?.[0] },
    variants,
  ) as VariantObj;

  return pickBy(allObject, (p) => p); // remove undefined values
};

type VariantBadgeT = { name: string; value: string };

export type ReplacedProduct = SearchOffers & {
  /** id of the more expensive offer that was replaced in the UI. if `null` then the offer was added for UI purpose. */
  replacedFromCondition?: ReebeloConditions;
  serviceProvider?: string;
  batteryUpgradePct?: string | null;
  batteryUpgradePrice?: string;
  badge?: { condition?: VariantBadgeT; batteryHealth?: VariantBadgeT };
};

export const keepCheapestOfferForSameCondition = (
  offers: ReplacedProduct[],
  _rVariants: ProductVariant[],
  shouldUseRVariants: boolean,
  dynamicVariants?: string[] | null,
): ReplacedProduct[] => {
  const language = REEBELO_STORE_TO_LANGUAGE[settings.store];
  const RC = Conditions[language] as Record<
    ReebeloConditions,
    ReebeloConditions
  >;

  const ORDER: ReebeloConditions[] = [
    ReebeloConditions.BrandNew,
    ReebeloConditions.Premium,
    ReebeloConditions.Pristine,
    ReebeloConditions.Excellent,
    ReebeloConditions.Good,
    ReebeloConditions.Acceptable,
  ];

  const rVariantHandles = _rVariants.map((v) => v.handle);

  const getVariantHandlesForOffer = (offer: ReplacedProduct): string[] => {
    if (offer?.categories?.[0] === 'Smartwatches' && !shouldUseRVariants) {
      return SMARTWATCHVARIANTS;
    }

    return shouldUseRVariants ? rVariantHandles : dynamicVariants ?? VARIANTS;
  };

  const buildGroupKey = (offer: ReplacedProduct): string =>
    getVariantHandlesForOffer(offer)
      .filter((h) => h !== 'condition')
      .map((h) =>
        shouldUseRVariants
          ? offer.rVariants?.find((rv) => rv.handle === h)?.value
          : offer[h as keyof ReplacedProduct],
      )
      .join('#');

  const normalizeConditionTag = (
    offer: ReplacedProduct | undefined,
    cond: ReebeloConditions,
  ): ReplacedProduct | undefined => {
    if (!offer) {
      return undefined;
    }
    const tags = (offer.tags ?? []).filter((t) => !t.startsWith('Condition_'));

    return { ...offer, tags: tags.concat(`Condition_${RC[cond]}`) };
  };

  const groups = Object.values(groupBy(offers, buildGroupKey));

  return groups.flatMap((group) => {
    const isSmartphones = group.some((p) =>
      p.tags?.includes('Category_Smartphones'),
    );

    return ORDER.map((cond) => {
      const offer = group.find((p) =>
        p.tags?.includes(`Condition_${RC[cond]}`),
      );

      if (cond === ReebeloConditions.Good) {
        return isNaStore && !isSmartphones
          ? offer
          : normalizeConditionTag(offer, cond);
      }

      return normalizeConditionTag(offer, cond);
    }).filter(Boolean) as ReplacedProduct[];
  });
};

export const selectionToString = (
  selection: SelectionT,
  category: string,
  dynamicVariants?: string[],
): string => {
  let variants: VariantNames | string[] = dynamicVariants ?? VARIANTS;

  if (category === 'Smartwatches') {
    variants = SMARTWATCHVARIANTS;
  }

  return variants
    .map((v) => selection[v as keyof SelectionT])
    .filter((str): str is string => str != null)
    .map((str) => str.toLocaleLowerCase().replace(/ +/g, '-'))
    .join('-');
};

type OfferTreeT = Record<string, ReplacedProduct>;

export class OfferTree {
  tree: OfferTreeT;

  orderedPath: string[];

  dynamicVariants?: string[] | null;

  private extraParams: Record<string, string>;

  constructor(
    offers: ReplacedProduct[],
    private category: string,
    dynamicVariantGroupsNames?: string[] | null,
    extraParams: { handle?: string } = {},
  ) {
    this.dynamicVariants = dynamicVariantGroupsNames;
    this.extraParams = extraParams;
    this.category = category;
    const pathToOffer: OfferTreeT = {};

    offers.forEach((offer) => {
      const tags = extractTags(offer.tags);
      const path = this.toPath({
        ...tags,
        serviceProvider: offer.serviceProvider,
      });

      pathToOffer[path] = offer;
    });
    this.tree = pathToOffer;
    this.orderedPath = Object.keys(pathToOffer).sort(
      (pathA, pathB) => pathToOffer[pathA].price - pathToOffer[pathB].price,
    );
  }

  filter({ serviceProvider }: { serviceProvider: string | undefined }) {
    // If no service provider is provided, return the current instance
    if (!serviceProvider) {
      return this;
    }

    // Filter the orderedPath based on the provided serviceProvider
    const filteredPath = this.orderedPath.filter((o) =>
      o.includes(serviceProvider),
    );

    // Create a new OfferTree instance with the filtered path
    const filteredOffers = filteredPath.map((path) => this.tree[path]);

    return new OfferTree(
      filteredOffers,
      this.category,
      this.dynamicVariants,
      this.extraParams,
    );
  }

  generateVariants = (category: string) => {
    if (category === 'Smartwatches') {
      return SMARTWATCHVARIANTS;
    }

    if (category === 'Laptops') {
      return LAPTOPSVARIANTS;
    }

    return this.dynamicVariants ?? VARIANTS;
  };

  get(selection: Partial<VariantObj>) {
    const path = this.toPath(selection);
    const directGet = this.tree[path];

    if (directGet != null) {
      return directGet;
    }

    // If the path is 64GB.Blue.undefined.undefined.undefined.Good, we generate the follwing path:
    // [64GB.Blue.undefined.undefined.undefined, 64GB.Blue.undefined, 64GB.Blue, 64GB, '']
    const variants = this.generateVariants(this.category);

    const candidatePaths = path
      .split(',')
      .map((_, index, arr) =>
        arr.slice(0, variants.length - index - 1).join(','),
      );

    const electedPath = candidatePaths.find((c) =>
      this.orderedPath.find((p) => p.startsWith(c)),
    ) as string; // can't be undefined since there are offers
    const potentialPaths = this.orderedPath.filter((p) =>
      p.startsWith(electedPath),
    );
    const sortedPath = sortBy(potentialPaths, (p) =>
      // path with the most common value with  the selection will be selected
      p
        .split(',')
        .map((k, i) => Number(k === path[i]))
        .reduce((acc, val) => acc + val),
    );

    return this.tree[sortedPath[0]];
  }

  getFrom(
    nextSelection: Partial<VariantObj>,
    currentSelection: Partial<VariantObj>,
    index?: number,
  ):
    | null
    | (SelectionT & { replacedFromCondition?: string; suggested?: boolean }) {
    const reg = /(,1?undefined)+$/g; // trim `undefined` at the end
    const nextPath = this.toPath(nextSelection).replace(reg, '');

    const isOutOfStock =
      this.orderedPath.find((o) =>
        o
          .replace(/Bluetooth \+ LTE/, 'LTE + Bluetooth')
          .startsWith(nextPath.replace(/Bluetooth \+ LTE/, 'LTE + Bluetooth')),
      ) == null;

    // If nextPath is set to "Stainless Steel.40mm.Gold.Bluetooth," and the orderedPath contains "Stainless Steel.40mm.Gold.Bluetooth + LTE.Leather.Leather Strap.Pink.1Acceptable.4GB," the current implementation returns false, which is incorrect. Therefore, modifing the string from "Bluetooth + LTE" to "LTE + Bluetooth" to ensure the correct result.

    const replacedSelection = { ...currentSelection, ...nextSelection };

    const replacedPath = this.toPath(replacedSelection).replace(reg, '');
    const cheapestMatchedPath = this.orderedPath.find((o) =>
      o.startsWith(replacedPath),
    );

    if (isOutOfStock) {
      const target = nextPath.split(',');
      let hasTargetPath;

      if (index) {
        hasTargetPath = this.orderedPath.find((path) => {
          const p = path.split(',');

          return p[index] === target[target.length - 1];
        });
      } else {
        hasTargetPath = this.orderedPath.find(
          (o) =>
            o.includes(`,${target[target.length - 1]},`) ||
            o.startsWith(`${target[target.length - 1]},`) ||
            o.endsWith(`,${target[target.length - 1]}`),
        );
      }

      if (hasTargetPath == null) {
        return null;
      }

      if (hasTargetPath) {
        const offer = _get(this.tree, hasTargetPath.replace(reg, ''));

        if (!offer) {
          return null;
        }

        return {
          ...this.extraParams,
          ...tagToSelection(offer.tags, this.dynamicVariants),
          serviceProvider: offer.serviceProvider,
          replacedFromCondition: offer.replacedFromCondition,
          suggested: true,
        };
      }
    }

    const offer =
      cheapestMatchedPath == null
        ? this.get(replacedSelection) // path is has been found
        : _get(this.tree, cheapestMatchedPath);

    if (offer == null) {
      return null;
    }

    return {
      ...this.extraParams,
      ...tagToSelection(offer.tags, this.dynamicVariants),
      serviceProvider: offer.serviceProvider,
      replacedFromCondition: offer.replacedFromCondition,
      suggested: false,
    };
  }

  /**
   * Convert a selection into JSON path like `64GB.Blue.undefined.undefined.undefined.1Good`
   */
  private toPath = (selection: Partial<VariantObj>) => {
    // condition is formated tobe sortable so in case of same price, we fallback on the best condition
    const SORTABLE_COND: Record<string, number> = {
      undefined: 1,
      [ReebeloConditions.Acceptable]: 1,
      [ReebeloConditions.Good]: 2,
      [ReebeloConditions.Excellent]: 3,
      [ReebeloConditions.Pristine]: 4,
      [ReebeloConditions.BrandNew]: 5,
    };
    const origCondition = selection.condition?.replace('Very Good', 'Good');
    const condition = `${
      SORTABLE_COND[origCondition || 'undefined']
    }${origCondition}`;
    const formatedSelection: Record<string, string> = {
      ...selection,
      condition,
      batteryHealth: selection?.batteryHealth ?? 'undefined',
    };

    if (this.category === 'Smartwatches') {
      return SMARTWATCHVARIANTS.map(
        (v) => formatedSelection[v] || 'undefined',
      ).join(',');
    }

    if (this.category === 'Laptops') {
      return LAPTOPSVARIANTS.map(
        (v) => formatedSelection[v] || 'undefined',
      ).join(',');
    }

    const variants = this.dynamicVariants ?? VARIANTS;
    const variantsArray = variants.map(
      (v) => formatedSelection[v] || 'undefined',
    );

    const batteryHealthValue = variantsArray[variantsArray.length - 1];

    const finalPath = variantsArray
      .slice(
        0,
        batteryHealthValue === 'undefined'
          ? variantsArray.length - 1
          : variantsArray.length,
      )
      .join(',');

    return finalPath;
  };
}
